import type { CSSProperties } from "react";

/**
 * Width-containment styles for markdown-rendered content.
 *
 * ## Why these are inline styles and not classes
 *
 * Production customer sites are served a **safelist-compiled** Tailwind
 * stylesheet: a utility class that was not present in the extraction snapshot
 * ships with no rule at all. Inline styles are immune to that failure mode, so
 * every guard in this module is expressed as a plain style object.
 *
 * ## The failure this prevents
 *
 * A single wide descendant (a GFM table, a `<pre>` with a long line, a raw
 * `<iframe width="1200">`, an unbroken URL) has a min-content width larger than
 * a phone viewport. When the markdown output is nested inside a CSS grid or flex
 * item whose `min-width` resolves to `auto`, that min-content width becomes the
 * track's base size, the *whole column* grows past the viewport, and every
 * sibling in the layout (headline, byline, floating navbar) is dragged wide with
 * it. The page then scrolls horizontally and looks broken - not just the table.
 *
 * The guards below are layered so no single element can do that:
 *
 * 1. `MARKDOWN_CONTENT_GUARD_STYLE` on the markdown wrapper - caps the content
 *    box at its parent's width, allows long tokens to break, and clips (never
 *    scrolls) anything that still escapes.
 * 2. `SCROLL_CONTAINER_GUARD_STYLE` on tables and code blocks - the wide child
 *    scrolls *inside* the article column instead of widening the page.
 * 3. `MEDIA_GUARD_STYLE` on images and iframes - intrinsic/attribute widths can
 *    never exceed the column.
 *
 * @packageDocumentation
 */

/**
 * Applied to the markdown wrapper element (the `wrapper` option of
 * {@link Markdown}, a `<div>` by default).
 *
 * - `minWidth: 0` - lets the wrapper shrink when it is itself a flex/grid item.
 * - `maxWidth: "100%"` - never wider than the column it was given.
 * - `overflowWrap: "break-word"` - long URLs and unbroken words wrap instead of
 *   spilling out of the column.
 * - `overflowX: "clip"` - last-resort containment. `clip` is used rather than
 *   `hidden`/`auto` because it does **not** create a scroll container, so it
 *   cannot introduce a second horizontal scrollbar, cannot trap the page's
 *   scroll position, and leaves the vertical axis `visible` (so `position:
 *   sticky` and overflowing decorations elsewhere keep working). Browsers that
 *   do not support `clip` simply drop the declaration and fall back to the
 *   other guards.
 */
export const MARKDOWN_CONTENT_GUARD_STYLE: CSSProperties = {
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "break-word",
  overflowX: "clip",
};

/**
 * Applied to the scroll wrapper around a table, and to `<pre>` blocks.
 *
 * The wide child is allowed to keep its natural width but scrolls *inside* this
 * box. `overscrollBehaviorX: "contain"` stops a horizontal swipe inside a table
 * from chaining out to the page/back-navigation gesture on touch devices.
 */
export const SCROLL_CONTAINER_GUARD_STYLE: CSSProperties = {
  maxWidth: "100%",
  minWidth: 0,
  overflowX: "auto",
  overflowY: "hidden",
  overscrollBehaviorX: "contain",
};

/**
 * Applied to replaced elements rendered from markdown whose height must be left
 * alone - currently `<iframe>`, where the embed's declared height is meaningful.
 *
 * Markdown routinely carries raw embed HTML with a hard-coded `width="1200"`,
 * which on a phone would widen the article column and the whole page.
 */
export const MEDIA_GUARD_STYLE: CSSProperties = {
  maxWidth: "100%",
};

/**
 * Applied to `<img>`.
 *
 * An image's intrinsic width is unbounded, so it is capped to the column. `height:
 * auto` is required alongside the cap: without it, a `height` attribute (or a
 * `height` from raw HTML) is honoured literally while the width is clamped, and
 * the image renders distorted.
 *
 * Consumer sites that ship Tailwind Preflight already get `img { max-width: 100%;
 * height: auto }`. This guard makes the behaviour independent of the host
 * stylesheet.
 */
export const IMAGE_GUARD_STYLE: CSSProperties = {
  maxWidth: "100%",
  height: "auto",
};

/**
 * Applied to inline `<code>`.
 *
 * `anywhere` (rather than `break-word`) is deliberate: it also reduces the
 * element's min-content contribution, so a long inline token cannot inflate an
 * ancestor grid/flex track even before the overflow guards engage.
 */
export const INLINE_CODE_GUARD_STYLE: CSSProperties = {
  overflowWrap: "anywhere",
};

/**
 * Merge a guard style with a caller-supplied `style` prop.
 *
 * Caller values win, so a consumer can always opt out of an individual
 * declaration (for example `style={{ overflowX: "visible" }}`) without having to
 * fork the component.
 */
export function mergeGuardStyle(
  guard: CSSProperties,
  style?: CSSProperties,
): CSSProperties {
  return style ? { ...guard, ...style } : guard;
}
